import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  cybersecImg1,
  devopsImg2,
  softwareEngImg1,
} from "../../../assets";

const tracks = [
  {
    id: "01",
    eyebrow: "BUILD",
    title: "Software Engineering",
    description:
      "Go from ideas to production-ready software. Learn how to design, build, test and deploy modern digital products used by real businesses.",
    image: softwareEngImg1,
    color: "#59b5cd",
    courses: [
      {
        name: "Frontend Development",
        price: "₦200,000",
        duration: "2 Months",
        level: "Beginner Friendly",
        description:
          "Build beautiful, responsive interfaces and modern web applications using industry-standard tools.",
        to: "/software-engineering",
      },
      {
        name: "Backend Development",
        price: "₦250,000",
        duration: "2 Months",
        level: "Beginner Friendly",
        description:
          "Learn APIs, databases, authentication and server-side engineering by building real backend systems.",
        to: "/software-engineering",
      },
      {
        name: "Fullstack Development",
        price: "₦450,000",
        duration: "4 Months",
        level: "Career Track",
        description:
          "Master frontend and backend development and learn how to build complete production-ready applications.",
        to: "/software-engineering",
        featured: true,
      },
    ],
  },

  {
    id: "02",
    eyebrow: "INTELLIGENCE",
    title: "Data & Artificial Intelligence",
    description:
      "Turn data into decisions and intelligent systems. Build the analytical, engineering and AI skills powering the next generation of technology.",
    image: devopsImg2,
    color: "#ecb32c",
    courses: [
      {
        name: "Data Analytics",
        price: "₦300,000",
        duration: "2 Months",
        level: "Beginner Friendly",
        description:
          "Transform raw data into meaningful insights using Excel, SQL, Power BI and practical business analytics.",
        to: "/data-analysis",
      },
      {
        name: "Data Engineering",
        price: "₦450,000",
        duration: "4 Months",
        level: "Career Track",
        description:
          "Build scalable pipelines, databases and data infrastructure using modern engineering tools.",
        to: "/data-science",
        featured: true,
      },
      {
        name: "Artificial Intelligence",
        price: "₦500,000",
        duration: "4 Months",
        level: "Career Track",
        description:
          "Learn machine learning, neural networks and intelligent applications through hands-on projects.",
        to: "/web-development",
      },
    ],
  },

  {
    id: "03",
    eyebrow: "DEFEND",
    title: "Cybersecurity",
    description:
      "Learn how modern organizations protect systems, networks and data from an increasingly sophisticated threat landscape.",
    image: cybersecImg1,
    color: "#59b5cd",
    courses: [
      {
        name: "Cybersecurity",
        price: "₦450,000",
        duration: "3 Months",
        level: "Career Track",
        description:
          "Build strong cybersecurity foundations covering threats, defense, security operations and system protection.",
        to: "/cyber-security",
        featured: true,
      },
      {
        name: "Ethical Hacking",
        price: "₦500,000",
        duration: "3.5 Months",
        level: "Advanced Track",
        description:
          "Learn penetration testing, vulnerability assessment and ethical hacking methodologies.",
        to: "/ceh",
      },
    ],
  },
];

const fadeUp = {
  hidden: {
    opacity: 0,
    y: 30,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.65,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

const AllCourses = () => {
  const navigate = useNavigate();

  return (
    <main className="bg-[#fafcfd] text-[#101418] overflow-hidden">




      {/* =========================================================
          TRACKS
      ========================================================== */}

      <section
        id="career-tracks"
        className="py-20 md:py-28"
      >

        <div className="max-w-7xl mx-auto px-6">

          {/* Section heading */}

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeUp}
            className="mb-16"
          >

            <p className="text-[#59b5cd] font-bold tracking-[0.2em] uppercase text-xs mb-4">
              CHOOSE YOUR PATH
            </p>

            <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">

              <h2 className="text-4xl md:text-6xl font-black tracking-[-0.04em] leading-none">
                Your career.
                <br />
                <span className="text-gray-400">
                  Your direction.
                </span>
              </h2>

              <p className="max-w-md text-gray-600 leading-relaxed">
                Whether you want to build products, engineer intelligent
                systems, analyze data or defend digital infrastructure,
                there is a path for you at Zulfah.
              </p>

            </div>

          </motion.div>


          {/* Track sections */}

          <div className="space-y-24">

            {tracks.map((track, trackIndex) => (

              <motion.section
                key={track.id}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-80px" }}
                variants={fadeUp}
              >

                {/* Track Header */}

                <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-10 items-center mb-10">

                  {/* Image */}

                  <div className="relative group">

                    <div
                      className="absolute inset-0 rounded-[2rem] blur-2xl opacity-20 group-hover:opacity-40 transition-opacity duration-500"
                      style={{ background: track.color }}
                    />

                    <div className="relative h-[260px] md:h-[330px] rounded-[2rem] overflow-hidden bg-gray-900">

                      <img
                        src={track.image}
                        alt={track.title}
                        className="w-full h-full object-cover opacity-80 group-hover:scale-105 transition-transform duration-700"
                      />

                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />

                      <div className="absolute top-6 left-6">

                        <span className="text-white/70 text-sm font-bold">
                          {track.id}
                        </span>

                      </div>

                      <div className="absolute bottom-6 left-6 right-6">

                        <p
                          className="text-xs tracking-[0.2em] font-bold mb-2"
                          style={{ color: track.color }}
                        >
                          {track.eyebrow}
                        </p>

                        <h3 className="text-3xl md:text-4xl text-white font-black">
                          {track.title}
                        </h3>

                      </div>

                    </div>

                  </div>


                  {/* Description */}

                  <div>

                    <p className="text-xl md:text-2xl leading-relaxed text-gray-700 max-w-2xl">
                      {track.description}
                    </p>

                    <div className="flex flex-wrap gap-3 mt-7">

                      <span className="px-4 py-2 rounded-full bg-[#59b5cd]/10 text-[#076c93] text-sm font-semibold">
                        Practical Projects
                      </span>

                      <span className="px-4 py-2 rounded-full bg-[#ecb32c]/10 text-[#9b7410] text-sm font-semibold">
                        Industry Skills
                      </span>

                      <span className="px-4 py-2 rounded-full bg-gray-100 text-gray-700 text-sm font-semibold">
                        Mentorship
                      </span>

                    </div>

                  </div>

                </div>


                {/* Course cards */}

                <div
                  className={`grid grid-cols-1 ${
                    track.courses.length === 2
                      ? "md:grid-cols-2"
                      : "md:grid-cols-3"
                  } gap-5`}
                >

                  {track.courses.map((course, index) => (

                    <motion.article
                      key={course.name}
                      whileHover={{
                        y: -8,
                      }}
                      transition={{
                        type: "spring",
                        stiffness: 250,
                        damping: 20,
                      }}
                      className={`relative group rounded-[1.5rem] border overflow-hidden transition-all duration-300 ${
                        course.featured
                          ? "bg-[#101418] border-[#101418] text-white shadow-2xl"
                          : "bg-white border-gray-200 hover:border-[#59b5cd]/40 hover:shadow-xl"
                      }`}
                    >

                      {/* Featured label */}

                      {course.featured && (
                        <div className="absolute top-5 right-5">

                          <span className="px-3 py-1.5 rounded-full bg-[#ecb32c] text-[#101418] text-[10px] font-black uppercase tracking-wider">
                            Popular
                          </span>

                        </div>
                      )}

                      <div className="p-7">

                        {/* Number */}

                        <div className="flex items-center justify-between mb-10">

                          <span
                            className={`text-sm font-bold ${
                              course.featured
                                ? "text-[#59b5cd]"
                                : "text-gray-400"
                            }`}
                          >
                            0{index + 1}
                          </span>

                          <span
                            className={`text-xs font-semibold ${
                              course.featured
                                ? "text-white/50"
                                : "text-gray-400"
                            }`}
                          >
                            {course.level}
                          </span>

                        </div>


                        <h4 className="text-2xl font-black mb-4">
                          {course.name}
                        </h4>


                        <p
                          className={`text-sm leading-relaxed min-h-[75px] ${
                            course.featured
                              ? "text-white/60"
                              : "text-gray-500"
                          }`}
                        >
                          {course.description}
                        </p>


                        {/* Meta */}

                        <div
                          className={`grid grid-cols-2 gap-4 mt-7 pt-6 border-t ${
                            course.featured
                              ? "border-white/10"
                              : "border-gray-100"
                          }`}
                        >

                          <div>

                            <p
                              className={`text-xs ${
                                course.featured
                                  ? "text-white/40"
                                  : "text-gray-400"
                              }`}
                            >
                              Duration
                            </p>

                            <p className="font-bold mt-1">
                              {course.duration}
                            </p>

                          </div>

                          <div>

                            <p
                              className={`text-xs ${
                                course.featured
                                  ? "text-white/40"
                                  : "text-gray-400"
                              }`}
                            >
                              Investment
                            </p>

                            <p
                              className="font-black mt-1"
                              style={{
                                color: course.featured
                                  ? "#ecb32c"
                                  : "#076c93",
                              }}
                            >
                              {course.price}
                            </p>

                          </div>

                        </div>


                        {/* CTA */}

                        <button
                          onClick={() => navigate(course.to)}
                          className={`w-full mt-7 py-3.5 rounded-xl font-bold transition-all duration-300 flex items-center justify-center gap-2 group-hover:gap-4 ${
                            course.featured
                              ? "bg-[#ecb32c] text-[#101418] hover:bg-white"
                              : "bg-[#101418] text-white hover:bg-[#59b5cd]"
                          }`}
                        >

                          Explore Program

                          <span className="transition-all">
                            →
                          </span>

                        </button>

                      </div>

                    </motion.article>

                  ))}

                </div>

              </motion.section>

            ))}

          </div>

        </div>

      </section>


      {/* =========================================================
          BOTTOM CTA
      ========================================================== */}

      <section className="relative py-24 md:py-32 bg-[#101418] overflow-hidden">

        {/* Glow */}

        <div className="absolute w-[500px] h-[500px] rounded-full bg-[#59b5cd] blur-[150px] opacity-20 -top-60 -left-40" />

        <div className="absolute w-[500px] h-[500px] rounded-full bg-[#ecb32c] blur-[150px] opacity-10 -bottom-60 -right-40" />

        <div className="relative max-w-5xl mx-auto px-6 text-center">

          <p className="text-[#59b5cd] text-xs font-bold uppercase tracking-[0.25em] mb-6">
            YOUR MOVE
          </p>

          <h2 className="text-white text-4xl md:text-6xl lg:text-7xl font-black tracking-[-0.04em] leading-tight">
            The next version
            <br />
            <span className="text-[#ecb32c]">
              of your career starts here.
            </span>
          </h2>

          <p className="text-white/50 max-w-xl mx-auto mt-7 text-lg leading-relaxed">
            Stop watching the technology industry evolve from the sidelines.
            Build the skills to become part of it.
          </p>

          <div className="flex flex-col sm:flex-row justify-center gap-4 mt-10">

            <button
              onClick={() => navigate("/get-started")}
              className="px-8 py-4 rounded-full bg-[#ecb32c] text-[#101418] font-black hover:bg-white transition-all duration-300"
            >
              Start Your Journey →
            </button>

            <button
              onClick={() =>
                document
                  .getElementById("career-tracks")
                  ?.scrollIntoView({ behavior: "smooth" })
              }
              className="px-8 py-4 rounded-full border border-white/20 text-white font-bold hover:bg-white/10 transition-all duration-300"
            >
              Explore Programs
            </button>

          </div>

        </div>

      </section>

    </main>
  );
};

export default AllCourses;